<script lang="ts">
	import { Input, Select, type SelectOptionType } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { Operator, type Behavior, type Component, type StateTrigger } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import type { BehaviorProps } from "#lib/components/flow/editor/builder/properties/page/constants.js";
	import type { Registry } from "#lib/components/page/registry.js";
	import { sentenceCase } from "#lib/string.js";

	const registry: Registry = getContext("registry");

	const { page, behavior, onChange }: BehaviorProps = $props();

	const components = $derived(flattenComponents(page?.components ?? []));
	const componentOptions: SelectOptionType<string>[] = $derived(
		components.map(component => ({
			name: registry.getDefinition(component.typeUri)?.displayName ?? sentenceCase(component.id),
			value: component.id,
		})),
	);
	const operatorOptions: SelectOptionType<Operator>[] = $derived([
		{ name: "Equal", value: Operator.EQUALS },
		{ name: "Not Equal", value: Operator.NOT_EQUALS },
	]);

	function flattenComponents(components: Component[]): Component[] {
		return components.reduce((acc: Component[], component: Component) => {
			const definition = registry.getDefinition(component.typeUri);
			if (!definition?.isContainer) {
				acc.push(component);
			}
			if (component.children) {
				acc.push(...flattenComponents(component.children));
			}
			return acc;
		}, []);
	}

	function handleComponentChange(event: Event) {
		const componentId = (event.target as HTMLSelectElement).value;
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			trigger.component = componentId;
			onChange?.(behavior);
		}
	}

	function handleOperatorChange(event: Event) {
		const value = (event.target as HTMLSelectElement).value;
		const operator = parseInt(value) as Operator;
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			trigger.operator = operator;
			onChange?.(behavior);
		}
	}

	function handleValueChange(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			trigger.state = value;
			onChange?.(behavior);
		}
	}

	function getComponent(behavior: Behavior): string {
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			return trigger.component ?? "";
		}
		return "";
	}

	function getOperator(behavior: Behavior): Operator {
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			const operator = trigger.operator;
			if (operator) {
				return operator;
			}
		}
		return Operator.EQUALS;
	}

	function getState(behavior: Behavior): string {
		const trigger = behavior.trigger?.value?.value as StateTrigger;
		if (trigger) {
			return trigger.state ?? "";
		}
		return "";
	}
</script>

<form class="grid grid-cols-2 gap-2">
	<div class="col-span-2 flex min-w-0 flex-1 flex-col gap-1">
		<Label for="component" class="text-sm font-medium text-gray-700">Component</Label>
		<Select id="component" value={getComponent(behavior)} items={componentOptions} onchange={e => handleComponentChange(e)} />
	</div>
	<div class="flex min-w-0 flex-1 flex-col gap-1">
		<Label for="operator" class="text-sm font-medium text-gray-700">Operator</Label>
		<Select id="operator" items={operatorOptions} value={getOperator(behavior)} onchange={e => handleOperatorChange(e)} />
	</div>
	<div class="flex min-w-0 flex-1 flex-col gap-1">
		<Label for="state" class="text-sm font-medium text-gray-700">State</Label>
		<Input id="state" value={getState(behavior)} onchange={e => handleValueChange(e)} />
	</div>
</form>
